import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { UserPlus, Shield, Trash2, Ban, RotateCcw, Clock, Crown, SlidersHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { PageLoading, PageHeader } from "@/components/shared";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const SECTIONS = [
  ["employees", "empNav.employees"],
  ["documents", "empNav.documents"],
  ["deadlines", "empNav.deadlines"],
  ["time", "time.nav"],
  ["renewals", "guide.navRenewals"],
  ["compliance-news", "ai.newsNav"],
  ["billing", "bill.navBilling"],
  ["activity", "empNav.activity"],
  ["company", "empNav.company"],
];

export default function Team() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ email: "", first_name: "", last_name: "" });

  // access management
  const [accessMember, setAccessMember] = useState(null);
  const [accessFull, setAccessFull] = useState(true);
  const [accessSel, setAccessSel] = useState([]);

  const load = () => api.get("/employer/team").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  const isOwnerRole = user?.role === "COMPANY_OWNER";
  const canManage = isOwnerRole || user?.role === "COMPANY_ADMIN";
  const lng = (i18n.language || "en").slice(0, 2);

  const invite = async () => {
    if (!form.email || !form.first_name || !form.last_name) { toast.error(t("tm.reqFields")); return; }
    setBusy(true);
    try {
      await api.post(`/employer/team/invite?lang=${lng}`, form);
      toast.success(t("tm.invited")); setOpen(false); setForm({ email: "", first_name: "", last_name: "" }); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const act = (fn) => async () => { try { await fn(); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };

  const openAccess = (m) => {
    setAccessMember(m);
    if (Array.isArray(m.section_access)) { setAccessFull(false); setAccessSel([...m.section_access]); }
    else { setAccessFull(true); setAccessSel(SECTIONS.map((s) => s[0])); }
  };
  const toggleSec = (key) => setAccessSel((prev) => prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]);
  const saveAccess = async () => {
    setBusy(true);
    try {
      await api.put(`/employer/team/${accessMember.id}/sections`, { section_access: accessFull ? null : accessSel });
      toast.success(t("tm.accessSaved")); setAccessMember(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  if (!data) return <PageLoading />;

  return (
    <div data-testid="team-page">
      <PageHeader title={t("tm.title")} subtitle={t("tm.subtitle")} action={canManage && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="rounded-full bg-navy text-white hover:bg-navy-muted" data-testid="team-invite-btn"><UserPlus className="mr-1 h-4 w-4" /> {t("tm.invite")}</Button></DialogTrigger>
          <DialogContent data-testid="team-invite-dialog">
            <DialogHeader><DialogTitle>{t("tm.invite")}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>{t("tm.firstName")}</Label><Input value={form.first_name} onChange={(e) => setForm((s) => ({ ...s, first_name: e.target.value }))} data-testid="team-first-input" /></div>
              <div className="space-y-2"><Label>{t("tm.lastName")}</Label><Input value={form.last_name} onChange={(e) => setForm((s) => ({ ...s, last_name: e.target.value }))} data-testid="team-last-input" /></div>
            </div>
            <div className="space-y-2"><Label>{t("tm.memberEmail")}</Label><Input type="email" value={form.email} onChange={(e) => setForm((s) => ({ ...s, email: e.target.value }))} data-testid="team-email-input" /></div>
            <DialogFooter><Button onClick={invite} disabled={busy} className="rounded-full bg-navy text-white hover:bg-navy-muted" data-testid="team-invite-submit">{t("tm.send")}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      )} />

      <div className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-400">{t("tm.members")}</div>
      <div className="space-y-2" data-testid="team-members">
        {data.members.map((m) => {
          const isOwner = m.role === "COMPANY_OWNER";
          const isSelf = m.id === user?.id;
          const disabled = m.status === "DISABLED";
          const restricted = Array.isArray(m.section_access);
          return (
            <div key={m.id} className="flex items-center justify-between rounded-2xl border border-slate-200 bg-white px-5 py-4" data-testid={`team-member-${m.id}`}>
              <div className="flex items-center gap-3">
                <span className={`flex h-9 w-9 items-center justify-center rounded-full ${isOwner ? "bg-amber-100 text-amber-600" : "bg-cyan/10 text-cyan-dark"}`}>{isOwner ? <Crown className="h-4 w-4" /> : <Shield className="h-4 w-4" />}</span>
                <div>
                  <div className="font-semibold text-navy">{m.first_name} {m.last_name} {isSelf && <span className="text-xs text-slate-400">({t("tm.you")})</span>}</div>
                  <div className="text-xs text-slate-400">
                    {m.email} · {isOwner ? t("tm.roleOwner") : t("tm.roleAdmin")}{disabled && ` · ${t("tm.disabled")}`}
                    {!isOwner && restricted && <span className="ml-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700" data-testid={`team-restricted-${m.id}`}>{t("tm.restricted")} ({m.section_access.length})</span>}
                  </div>
                </div>
              </div>
              {canManage && !isOwner && !isSelf && (
                <div className="flex items-center gap-2">
                  {isOwnerRole && (
                    <Button size="sm" variant="outline" className="rounded-full" onClick={() => openAccess(m)} data-testid={`team-access-${m.id}`}><SlidersHorizontal className="mr-1 h-3.5 w-3.5" /> {t("tm.access")}</Button>
                  )}
                  {disabled
                    ? <Button size="sm" variant="outline" className="rounded-full" onClick={act(() => api.post(`/employer/team/${m.id}/restore`))} data-testid={`team-restore-${m.id}`}><RotateCcw className="mr-1 h-3.5 w-3.5" /> {t("tm.restore")}</Button>
                    : <Button size="sm" variant="outline" className="rounded-full" onClick={act(() => api.post(`/employer/team/${m.id}/disable`))} data-testid={`team-disable-${m.id}`}><Ban className="mr-1 h-3.5 w-3.5" /> {t("tm.disable")}</Button>}
                  <Button size="sm" variant="ghost" className="rounded-full text-red-600 hover:bg-red-50" onClick={() => { if (window.confirm(t("tm.removeConfirm"))) act(() => api.delete(`/employer/team/${m.id}`))(); }} data-testid={`team-remove-${m.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {canManage && (
        <>
          <div className="mb-3 mt-8 text-xs font-semibold uppercase tracking-wider text-slate-400">{t("tm.pending")}</div>
          {data.invitations.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-200 px-5 py-4 text-sm text-slate-400">{t("tm.noPending")}</div>
          ) : (
            <div className="space-y-2" data-testid="team-invitations">
              {data.invitations.map((inv) => (
                <div key={inv.id} className="flex items-center justify-between rounded-2xl border border-slate-200 bg-white px-5 py-4">
                  <div className="flex items-center gap-3">
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-slate-400"><Clock className="h-4 w-4" /></span>
                    <div><div className="font-semibold text-navy">{inv.first_name} {inv.last_name}</div><div className="text-xs text-slate-400">{inv.invited_email}</div></div>
                  </div>
                  <Button size="sm" variant="ghost" className="rounded-full text-red-600 hover:bg-red-50" onClick={act(() => api.post(`/employer/team/invite/${inv.id}/revoke`))} data-testid={`team-revoke-${inv.id}`}>{t("tm.revoke")}</Button>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      <Dialog open={!!accessMember} onOpenChange={(v) => !v && setAccessMember(null)}>
        <DialogContent data-testid="team-access-dialog">
          <DialogHeader><DialogTitle>{t("tm.manageAccess")}{accessMember ? ` — ${accessMember.first_name} ${accessMember.last_name}` : ""}</DialogTitle></DialogHeader>
          <div className="flex items-center justify-between rounded-xl border border-slate-200 px-4 py-3">
            <div>
              <div className="text-sm font-semibold text-navy">{t("tm.fullAccess")}</div>
              <div className="text-xs text-slate-400">{t("tm.fullAccessHint")}</div>
            </div>
            <Switch checked={accessFull} onCheckedChange={setAccessFull} data-testid="team-access-full-switch" />
          </div>
          {!accessFull && (
            <div className="mt-2 space-y-2" data-testid="team-access-sections">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">{t("tm.modules")}</div>
              <div className="grid grid-cols-2 gap-2">
                {SECTIONS.map(([key, lk]) => (
                  <label key={key} className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm text-navy" data-testid={`team-access-opt-${key}`}>
                    <Checkbox checked={accessSel.includes(key)} onCheckedChange={() => toggleSec(key)} data-testid={`team-access-check-${key}`} />
                    {t(lk)}
                  </label>
                ))}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={saveAccess} disabled={busy} className="rounded-full bg-navy text-white hover:bg-navy-muted" data-testid="team-access-save">{t("tm.saveAccess")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
